import { Avatar, Empty, Tag, Tooltip, Typography } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  EditOutlined,
  FileTextOutlined,
  PlusOutlined,
  SendOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { format } from '@/theme/format';
import { semantic } from '@/theme/tokens';
import type { ReactNode } from 'react';

const { Text } = Typography;

export interface ActivityEntry {
  id: string;
  /** ISO date string */
  at: string;
  /** Short action keyword (e.g. "approved", "submitted", "edited") */
  action: string;
  /** Person who performed the action */
  user: string;
  /** Optional note / description */
  note?: string;
}

export interface ActivityTimelineProps {
  entries: ActivityEntry[];
  /** Empty-state copy when there are no entries */
  emptyText?: string;
}

/**
 * Map an action keyword to a brand colour + icon. Falls back to neutral
 * for unknown actions. Keep this list lean — adding too many specialised
 * actions makes the timeline feel busy.
 */
function actionTheme(action: string): { color: string; icon: ReactNode } {
  const a = action.toLowerCase();
  if (a.includes('approve')) return { color: semantic.status.success, icon: <CheckCircleOutlined /> };
  if (a.includes('reject') || a.includes('cancel'))
    return { color: semantic.status.error, icon: <CloseCircleOutlined /> };
  if (a.includes('submit') || a.includes('send'))
    return { color: semantic.brand.primary, icon: <SendOutlined /> };
  if (a.includes('edit') || a.includes('updat') || a.includes('revis'))
    return { color: semantic.brand.accent, icon: <EditOutlined /> };
  if (a.includes('creat') || a.includes('add'))
    return { color: semantic.status.info, icon: <PlusOutlined /> };
  if (a.includes('attach') || a.includes('upload') || a.includes('document'))
    return { color: semantic.status.info, icon: <FileTextOutlined /> };
  return { color: semantic.status.neutral, icon: <UserOutlined /> };
}

/**
 * Initials from a person's name. "Omar Nasser" → "ON". Falls back to
 * a single letter for one-word names; defaults to "?" if blank.
 */
function initials(name: string): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0]!.charAt(0).toUpperCase();
  return (parts[0]!.charAt(0) + parts[parts.length - 1]!.charAt(0)).toUpperCase();
}

/**
 * Stable colour per user — derived from name so the same person has the
 * same avatar tint across the app. Eight-bucket palette aligned to the
 * branded chart sequence so avatars don't introduce new hues.
 */
const AVATAR_PALETTE = [
  semantic.brand.primary,
  semantic.status.success,
  semantic.brand.accent,
  semantic.status.info,
  '#9C3961',
  '#6E4F8B',
  '#8A4A1F',
  '#1E6F6F',
];
function avatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  }
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length]!;
}

/**
 * Relative-time helper. Anchors against today (project's "today" =
 * 2026-08-31 per the demo fixture timeline) and falls back to absolute
 * date once the gap exceeds 30 days.
 */
function relativeTime(iso: string): string {
  const today = new Date('2026-08-31').getTime();
  const ts = new Date(iso).getTime();
  const days = Math.round((today - ts) / (24 * 60 * 60 * 1000));
  if (days < 0) return format.date(iso, 'short');
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return format.date(iso, 'short');
}

/**
 * Branded activity timeline — replaces both the bare AntD Timeline and
 * the bare Table activity views with a single consistent surface across
 * project, contract, and any future entity detail drawers.
 *
 * Each row:
 *   • coloured Avatar with user initials (stable per name)
 *   • action icon + capitalised action verb
 *   • user name in primary text
 *   • optional note in secondary text
 *   • relative-time on the right with the absolute date as a tooltip
 *
 * No external timeline / icon libs beyond what's already imported.
 */
export default function ActivityTimeline({
  entries,
  emptyText = 'No activity yet.',
}: ActivityTimelineProps) {
  if (entries.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={<Text type="secondary">{emptyText}</Text>}
      />
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      {/* Vertical guide line behind the avatars, brand-tinted. */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          insetInlineStart: 19,
          top: 12,
          bottom: 12,
          width: 2,
          background: 'rgba(31, 77, 58, 0.12)',
          borderRadius: 1,
        }}
      />
      {entries.map((e, idx) => {
        const theme = actionTheme(e.action);
        const isLast = idx === entries.length - 1;
        return (
          <div
            key={e.id}
            style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 12,
              paddingBottom: isLast ? 0 : 16,
            }}
          >
            <Avatar
              size={40}
              style={{
                backgroundColor: avatarColor(e.user),
                flexShrink: 0,
                fontSize: 13,
                fontWeight: 600,
                position: 'relative',
                zIndex: 1,
                border: '2px solid #fff',
                boxShadow: '0 0 0 1px rgba(0,0,0,0.04)',
              }}
            >
              {initials(e.user)}
            </Avatar>
            <div style={{ flex: 1, minWidth: 0, paddingTop: 4 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'space-between',
                  gap: 12,
                  flexWrap: 'wrap',
                }}
              >
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <Tag
                    color={theme.color}
                    style={{
                      margin: 0,
                      borderColor: theme.color,
                      background: `${theme.color}15`,
                      color: theme.color,
                      fontWeight: 500,
                      textTransform: 'capitalize',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    <span style={{ fontSize: 11 }}>{theme.icon}</span>
                    {e.action}
                  </Tag>
                  <Text style={{ fontSize: 13, fontWeight: 500 }}>{e.user}</Text>
                </div>
                <Tooltip title={format.date(e.at, 'long')}>
                  <Text type="secondary" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                    {relativeTime(e.at)}
                  </Text>
                </Tooltip>
              </div>
              {e.note && (
                <Text
                  type="secondary"
                  style={{ fontSize: 12, display: 'block', marginTop: 4, lineHeight: 1.5 }}
                >
                  {e.note}
                </Text>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
